export type Species = "dog" | "cat";
export type WeightUnit = "lb" | "kg";
export type DensityUnit = "kcal_per_kg" | "kcal_per_100g";

export type DogStatus =
  | "neutered_adult"
  | "intact_adult"
  | "weight_loss"
  | "obesity_prone"
  | "puppy_under_4m"
  | "puppy_over_4m";

export type CatStatus = "neutered_adult" | "intact_adult" | "weight_loss" | "kitten";

export type LifeStatus = DogStatus | CatStatus;

export interface MeasurementResult {
  gramsPerDay: number;
  cupsPerDay: number | null;
  gramsPerMeal: number;
  cupsPerMeal: number | null;
}

export interface FeedingPlanResult {
  weightKg: number;
  /** RER at rerWeightKg, the same weight the daily figure is based on. */
  rer: number;
  rerWeightKg: number;
  targetApplied: boolean;
  dailyKcal: number;
  foodDensityKcalPerKg: number;
  measurements: MeasurementResult;
}

export interface WeightTrendPoint {
  week: number;
  conservativeKg: number;
  assertiveKg: number;
}

export interface WeightTrendResult {
  applies: boolean;
  direction: "down" | "flat" | "up";
  model: "guideline" | "calorie_balance" | "monitoring";
  title: string;
  summary: string;
  weeklyRangeText: string;
  points: WeightTrendPoint[];
}

export const calculateRER = (weightKg: number): number => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) {
    throw new Error("Weight must be a positive number.");
  }

  return 70 * Math.pow(weightKg, 0.75);
};

/**
 * Life-stage factors applied to RER. Exported so page copy that lists them is generated from the
 * same numbers the calculator uses and cannot drift from them.
 */
export const MER_MULTIPLIERS: Record<Species, Record<string, number>> = {
  dog: { neutered_adult: 1.6, intact_adult: 1.8, weight_loss: 1.0, obesity_prone: 1.2, puppy_under_4m: 3.0, puppy_over_4m: 2.0 },
  cat: { neutered_adult: 1.2, intact_adult: 1.4, weight_loss: 0.8, kitten: 2.5 },
};

/**
 * Maintenance energy: RER x a life-stage multiplier. For weight loss, veterinary calculators
 * (e.g. the Pet Nutrition Alliance's) take RER at the pet's ideal or target weight, not its
 * current weight; applying the factor to current weight feeds an overweight pet more. When a
 * target below the current weight is given for weight loss, RER uses the target.
 */
export const resolveEnergyWeightKg = (status: string, weightKg: number, targetWeightKg?: number): number =>
  status === "weight_loss" && targetWeightKg !== undefined && Number.isFinite(targetWeightKg) && targetWeightKg > 0 && targetWeightKg < weightKg
    ? targetWeightKg
    : weightKg;

export const calculateMER = (species: Species, status: string, weightKg: number, targetWeightKg?: number): number => {
  const rer = calculateRER(resolveEnergyWeightKg(status, weightKg, targetWeightKg));
  const multiplier = MER_MULTIPLIERS[species][status];
  // A missing factor used to fall back to 1.0 silently, underfeeding a kitten by 60%.
  if (multiplier === undefined) throw new Error(`No energy factor for status "${status}" (${species}).`);

  return Math.round(rer * multiplier);
};

export const calculateMeasurements = (
  dailyKcal: number,
  foodDensityKcalPerKg: number,
  kcalPerCup?: number,
  mealsPerDay = 2,
): MeasurementResult => {
  if (!Number.isFinite(dailyKcal) || dailyKcal <= 0) {
    throw new Error("Daily calories must be a positive number.");
  }

  if (!Number.isFinite(foodDensityKcalPerKg) || foodDensityKcalPerKg <= 0) {
    throw new Error("Food calorie density must be a positive number.");
  }

  if (!Number.isFinite(mealsPerDay) || mealsPerDay < 1) {
    throw new Error("Meals per day must be at least 1.");
  }

  const gramsPerDay = Math.round((dailyKcal / foodDensityKcalPerKg) * 1000);
  const cupsPerDay =
    kcalPerCup && Number.isFinite(kcalPerCup) && kcalPerCup > 0
      ? Number((dailyKcal / kcalPerCup).toFixed(2))
      : null;

  return {
    gramsPerDay,
    cupsPerDay,
    gramsPerMeal: Math.round(gramsPerDay / mealsPerDay),
    cupsPerMeal: cupsPerDay === null ? null : Number((cupsPerDay / mealsPerDay).toFixed(2)),
  };
};

export const poundsToKg = (pounds: number) => pounds / 2.2046226218;

export const kgToPounds = (kg: number) => kg * 2.2046226218;

export const normalizeFoodDensity = (density: number, unit: DensityUnit): number => {
  if (!Number.isFinite(density) || density <= 0) {
    throw new Error("Food calorie density must be a positive number.");
  }

  return unit === "kcal_per_100g" ? density * 10 : density;
};

export const calculateFeedingPlan = ({
  species,
  status,
  weight,
  targetWeight,
  weightUnit,
  foodDensity,
  densityUnit,
  kcalPerCup,
  mealsPerDay,
}: {
  species: Species;
  status: string;
  weight: number;
  /** Optional target weight, same unit as `weight`; only used for weight loss. */
  targetWeight?: number;
  weightUnit: WeightUnit;
  foodDensity: number;
  densityUnit: DensityUnit;
  kcalPerCup?: number;
  mealsPerDay: number;
}): FeedingPlanResult => {
  if (!Number.isFinite(weight) || weight <= 0) {
    throw new Error("Weight must be a positive number.");
  }

  const weightKg = weightUnit === "kg" ? weight : poundsToKg(weight);
  const foodDensityKcalPerKg = normalizeFoodDensity(foodDensity, densityUnit);
  const targetKg = targetWeight === undefined ? undefined : weightUnit === "kg" ? targetWeight : poundsToKg(targetWeight);
  const rerWeightKg = resolveEnergyWeightKg(status, weightKg, targetKg);
  const rer = Math.round(calculateRER(rerWeightKg));
  const dailyKcal = calculateMER(species, status, weightKg, targetKg);
  const measurements = calculateMeasurements(dailyKcal, foodDensityKcalPerKg, kcalPerCup, mealsPerDay);

  return {
    weightKg,
    rer,
    rerWeightKg,
    targetApplied: rerWeightKg !== weightKg,
    dailyKcal,
    foodDensityKcalPerKg,
    measurements,
  };
};

/**
 * Reads the optional weight-loss target as typed. Returns the target to use (in the entered unit)
 * and a notice for the owner whenever the target is ignored or looks like a slip, so a target is
 * never dropped or applied without the owner being told.
 */
export const assessTargetWeight = (
  status: string,
  currentWeight: number,
  raw: string,
  unit: WeightUnit,
): { target: number | undefined; notice: string | null } => {
  if (status !== "weight_loss" || raw.trim() === "") return { target: undefined, notice: null };
  const target = Number(raw);
  if (!Number.isFinite(target) || target <= 0) {
    return { target: undefined, notice: "Enter the target as a number. Until then, this estimate uses the current weight." };
  }
  if (!(currentWeight > 0) || target >= currentWeight) {
    return { target: undefined, notice: "The target must be below the current weight, so this estimate uses the current weight." };
  }
  if (target < currentWeight * TARGET_CHECK_RATIO) {
    return {
      target,
      notice: `That target is well below the current weight. Check that it is in ${unit} and is the figure your vet gave you.`,
    };
  }
  return { target, notice: null };
};

/** Below this share of current weight a target is applied but flagged; a kg figure in a lb field lands near 0.45. */
const TARGET_CHECK_RATIO = 0.65;

const getWeeklyWeightLossRange = (species: Species, status: string): [number, number] | null => {
  if (status !== "weight_loss" && status !== "obesity_prone") return null;
  return species === "cat" ? [0.005, 0.01] : [0.01, 0.02];
};

export const calculateWeightTrend = (
  species: Species,
  status: string,
  weightKg: number,
  weeks: number,
  feedingAdjustmentPercent = 0,
  baselineDailyKcal?: number,
): WeightTrendResult => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) {
    throw new Error("Weight must be a positive number.");
  }

  if (!Number.isFinite(weeks) || weeks < 1 || weeks > 12) {
    throw new Error("Weeks must be between 1 and 12.");
  }

  const roundedWeeks = Math.round(weeks);
  const weeklyRange = feedingAdjustmentPercent <= 0 ? getWeeklyWeightLossRange(species, status) : null;
  const canUseCalorieBalance =
    feedingAdjustmentPercent !== 0 && Number.isFinite(baselineDailyKcal) && (baselineDailyKcal ?? 0) > 0;

  if (canUseCalorieBalance) {
    const kcalPerKgBodyMass = 7700;
    const dailyKcalDelta = ((baselineDailyKcal ?? 0) * feedingAdjustmentPercent) / 100;

    return {
      applies: true,
      direction: feedingAdjustmentPercent > 0 ? "up" : "down",
      model: "calorie_balance",
      title: feedingAdjustmentPercent > 0 ? "Higher-food scenario" : "Lower-food scenario",
      summary:
        "This rough calorie-balance line uses the selected portion change and a 7,700 kcal per kg body-mass approximation. Real weight change can differ because metabolism, activity, treats, body composition, and health all matter.",
      weeklyRangeText:
        feedingAdjustmentPercent > 0
          ? `Rough surplus: +${Math.round(dailyKcalDelta)} kcal/day`
          : `Rough deficit: ${Math.round(dailyKcalDelta)} kcal/day`,
      points: Array.from({ length: roundedWeeks + 1 }, (_, week) => ({
        week,
        conservativeKg: Number(weightKg.toFixed(2)),
        assertiveKg: Number(Math.max(0.1, weightKg + (dailyKcalDelta * 7 * week) / kcalPerKgBodyMass).toFixed(2)),
      })),
    };
  }

  if (feedingAdjustmentPercent > 0) {
    return {
      applies: false,
      direction: "up",
      model: "monitoring",
      title: "Higher-food scenario",
      summary:
        "A higher portion may increase weight over time, but this calculator needs a valid calorie estimate before it can show even a rough calorie-balance line.",
      weeklyRangeText: "Monitor for upward drift",
      points: Array.from({ length: roundedWeeks + 1 }, (_, week) => ({
        week,
        conservativeKg: Number(weightKg.toFixed(2)),
        assertiveKg: Number(weightKg.toFixed(2)),
      })),
    };
  }

  if (!weeklyRange) {
    return {
      applies: false,
      direction: feedingAdjustmentPercent < 0 ? "down" : "flat",
      model: "monitoring",
      title: "Trend monitoring",
      summary:
        feedingAdjustmentPercent < 0
          ? "This lower-food scenario may move weight downward, but a safe target pace depends on body condition and health. Use weekly weight checks instead of treating this as a forecast."
          : "This selection estimates daily feeding, but it does not provide enough clinical context to forecast weight change. Track weekly weight and body condition instead.",
      weeklyRangeText: feedingAdjustmentPercent < 0 ? "Monitor for gradual loss" : "No weight-change projection",
      points: Array.from({ length: roundedWeeks + 1 }, (_, week) => ({
        week,
        conservativeKg: Number(weightKg.toFixed(2)),
        assertiveKg: Number((weightKg * (1 - (feedingAdjustmentPercent < 0 ? 0.0025 : 0) * week)).toFixed(2)),
      })),
    };
  }

  const [conservativeRate, assertiveRate] = weeklyRange;

  return {
    applies: true,
    direction: "down",
    model: "guideline",
    title: "Guideline weight trend",
    summary:
      "This is a monitoring band based on commonly cited veterinary weight-loss rates, not a promise. Real progress depends on body condition, treats, activity, illness, and whether the target weight is correct.",
    weeklyRangeText:
      species === "cat" ? "Guideline pace: about 0.5-1% body weight per week" : "Guideline pace: about 1-2% body weight per week",
    points: Array.from({ length: roundedWeeks + 1 }, (_, week) => ({
      week,
      conservativeKg: Number((weightKg * Math.pow(1 - conservativeRate, week)).toFixed(2)),
      assertiveKg: Number((weightKg * Math.pow(1 - assertiveRate, week)).toFixed(2)),
    })),
  };
};

export const getWeightSanityWarning = (species: Species, weight: number, unit: WeightUnit): string | null => {
  if (!Number.isFinite(weight) || weight <= 0) return null;

  const weightKg = unit === "kg" ? weight : poundsToKg(weight);
  const displayWeight = unit === "kg" ? `${weight} kg` : `${weight} lb`;

  if (species === "cat" && weightKg > 15) {
    return `That is a very high weight for a cat (${displayWeight}). Please check that the weight and unit are correct.`;
  }

  if (species === "cat" && weightKg < 1) {
    return `That is a very low weight for a cat (${displayWeight}). Please check that the weight and unit are correct.`;
  }

  if (species === "dog" && weightKg > 90) {
    return `That is a very high weight for a dog (${displayWeight}). Please check that the weight and unit are correct.`;
  }

  if (species === "dog" && weightKg < 1) {
    return `That is a very low weight for a dog (${displayWeight}). Please check that the weight and unit are correct.`;
  }

  return null;
};

export const getFoodDensitySanityWarning = (density: number, unit: DensityUnit): string | null => {
  if (!Number.isFinite(density) || density <= 0) return null;

  const densityKcalPerKg = normalizeFoodDensity(density, unit);
  const displayDensity = unit === "kcal_per_100g" ? `${density} kcal/100g` : `${density} kcal/kg`;

  if (densityKcalPerKg < 300) {
    return `That calorie value is very low (${displayDensity}). Please check the label and unit. Wet foods are often lower than dry foods, but this is unusually low.`;
  }

  if (densityKcalPerKg > 8000) {
    return `That calorie value is very high (${displayDensity}). Please check whether the label says kcal/kg or kcal/100g.`;
  }

  return null;
};
